/* eslint-disable consistent-return */
import { ApiResponse, TList } from '../@types';
import type { ClassroomListRow } from './@types';
import { request } from '../../utils';

export interface ClassroomData {
  classroom_name: string;
  classroom_id: string;
  creator_id: string;
  creator_email?: string;
  archived?: boolean;
}

export interface teacherStudentConnection {
  teacher_id: string;
  student_id: string;
}

export interface collaborator {
  classroom_id: string;
  email: string;
}

export interface ClassCodeData {
  class_code: string | null;
  email: string | null;
}

export const getClassroomsList = async (
  skip: number,
  limit: number,
  params?: Record<string, string | undefined>,
): Promise<ApiResponse<TList<ClassroomListRow>>> => {
  return request<ApiResponse<TList<ClassroomListRow>>>({
    url: '/classrooms',
    method: 'GET',
    params: {
      limit,
      skip,
      ...params,
    },
  });
};

export const generateClassroomCsv = async (
  params?: Record<string, string | undefined>,
): Promise<Blob> => {
  const response = await request<BlobPart>({
    url: '/classrooms/get-csv',
    responseType: 'blob',
    params,
  });
  return new Blob([response]);
};

// Get all classrooms for the current teacher user (creatorId) and optionally only those
// that have been archived
export const getClassrooms = async (
  creatorId: string,
  archive: boolean,
): Promise<ApiResponse<any>> => {
  return request<ApiResponse<any>>({
    url: `classrooms/${creatorId}&${archive}`,
    method: 'GET',
  });
};

// DEPRECATED: Get all classrooms in database
export const getAllClassrooms = async (): Promise<ApiResponse<any>> => {
  return request<ApiResponse<any>>({
    url: `classrooms/all`,
    method: 'GET',
  });
};

// Get all Classrooms that a Student is a part of
export const getStudentClassrooms = async (userId: string): Promise<ApiResponse<any>> => {
  return request<ApiResponse<any>>({
    url: `classrooms/student-${userId}`,
    method: 'GET',
  });
};

// Get a specific Classroom based on the tab selected, which
// triggers a different query on the backend
export const getClassroom = async (
  classroomId: string | string[],
  queryType: string | string[],
): Promise<ApiResponse<any>> => {
  return request<ApiResponse<any>>({
    url: `classrooms/get/${classroomId}/${queryType}`,
    method: 'GET',
  });
};

// Get a Classroom with its Class Code
export const getClassroomWithCode = async (code: string | string[]): Promise<ApiResponse<any>> => {
  return request<ApiResponse<any>>({
    url: `classrooms/get/${code}`,
    method: 'GET',
  });
};

// Get all Students tied to a specific Teacher through their Classrooms
export const getStudentsForTeacher = async (teacherId: string): Promise<ApiResponse<any>> => {
  return request<ApiResponse<any>>({
    url: `classrooms/teacher-students/${teacherId}`,
    method: 'GET',
  });
};

// Send a request to make a new classroom
export const createClassroom = async (
  credential: Partial<ClassroomData>,
): Promise<ApiResponse<void>> => {
  return request<ApiResponse<void>>({
    url: 'classrooms/create',
    method: 'POST',
    data: credential,
  });
};

// Joins a classroom
export const joinClassroom = async (
  credential: Partial<ClassCodeData>,
): Promise<ApiResponse<void>> => {
  return request<ApiResponse<void>>({
    url: 'classrooms/join',
    method: 'POST',
    data: credential,
  });
};

// Invites a secondary person to help manage a Classroom
export const inviteCollaborator = async (
  credential: Partial<collaborator>,
): Promise<ApiResponse<void>> => {
  return request<ApiResponse<void>>({
    url: 'classrooms/invite_collaborator',
    method: 'POST',
    data: credential,
  });
};

// Get details regarding one student in a Classroom
export const getStudent = async (
  credential: Partial<teacherStudentConnection>,
): Promise<ApiResponse<any>> => {
  return request<ApiResponse<any>>({
    url: `classrooms/student`,
    method: 'POST',
    data: credential,
  });
};

// Changes a Classroom's status to archived
export const archiveClassroom = async (
  credential: Partial<ClassroomData>,
): Promise<ApiResponse<void>> => {
  return request<ApiResponse<void>>({
    url: 'classrooms/archive_classroom',
    method: 'POST',
    data: credential,
  });
};

// Sends a request to rename a Classroom
export const renameClassroom = async (
  credential: Partial<ClassroomData>,
): Promise<ApiResponse<void>> => {
  return request<ApiResponse<void>>({
    url: 'classrooms/rename_classroom',
    method: 'POST',
    data: credential,
  });
};

// Resets the class code for a classroom, generating a new code
export const resetCode = async (credential: Partial<ClassroomData>): Promise<ApiResponse<void>> => {
  return request<ApiResponse<void>>({
    url: 'classrooms/reset_code',
    method: 'POST',
    data: credential,
  });
};

// Deletes a Classroom
export const deleteClassroom = async (
  credential: Partial<ClassroomData>,
): Promise<ApiResponse<void>> => {
  return request<ApiResponse<void>>({
    url: 'classrooms',
    method: 'DELETE',
    data: credential,
  });
};

// Removes a student from a Classroom
export const removeStudent = async (
  credential: Partial<teacherStudentConnection>,
): Promise<ApiResponse<void>> => {
  return request<ApiResponse<void>>({
    url: 'classrooms/remove_student',
    method: 'DELETE',
    data: credential,
  });
};

export const exportSkillbuilderReport = async (
  filters: Partial<{
    classroom_id: string;
    timeRange: string;
  }>,
): Promise<Blob> => {
  return request<Blob>({
    url: 'report/classroom-skillbuilder',
    method: 'POST',
    data: filters,
    responseType: 'blob',
  });
};
